import { ANON_ROLE_UID } from "@buildwithflux/constants";
import { documentSubcollectionFields, intoBaseDocument, PermissionType, ProfileDocumentType, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
/**
 * Mock implementation of the document storage API which can be used to test the higher
 * level service objects.
 */
export class MockDocumentRepository {
    documents;
    constructor(documents = {}) {
        this.documents = documents;
    }
    createStream(_options) {
        return {
            fetchNextPage(onSuccess, _onError) {
                onSuccess([]);
                return () => { };
            },
            reset() { },
        };
    }
    async getBaseDataByUid(_documentUid) {
        return undefined;
    }
    async getBaseChunk(_options = {}) {
        return [];
    }
    async getBaseDataByForkedFromDocumentUid(_forkOfDocumentUid, _currentUserUid, _organizationMemberships) {
        return [];
    }
    async migrate(_documentUid, _targetVersion) { }
    async saveLayerViewConfig(documentUid, layerViewConfig) {
        const document = {
            ...this.documents[documentUid],
            // HACK: to save layerViewConfig for the mock. layerViewConfig
            // saving is normally done by PcbLayerViewStore.
            layerViewConfig: layerViewConfig,
        };
        this.save(document);
    }
    async save(document, collectionsToSave = documentSubcollectionFields, _withReferenceToCachedValue) {
        const stored = this.documents[document.uid];
        if (stored) {
            this.documents[document.uid] = {
                ...stored,
                ...intoBaseDocument(document),
                ...Object.fromEntries(collectionsToSave.flatMap((collection) => {
                    return document[collection]
                        ? [
                            [
                                collection,
                                {
                                    ...stored[collection],
                                    ...document[collection],
                                },
                            ],
                        ]
                        : [];
                })),
            };
        }
        else {
            this.documents[document.uid] = document;
        }
    }
    saveWithMetadata(document) {
        return this.save(document.documentData);
    }
    async update(documentUid, fields) {
        const stored = this.documents[documentUid];
        if (!stored) {
            throw new Error(`Document with uid ${documentUid} not found`);
        }
        Object.assign(stored, fields);
        return undefined;
    }
    async getAllBaseDataByOwner(ownerUid, currentUserUid, currentOrganizationMemberships, _documentTypeFilter = ProfileDocumentType.all, _includeArchived = false) {
        return [];
    }
    async getDataForCurrentUser(documentUid, _currentUserUid) {
        return this.documents[documentUid];
    }
    async getBaseWithoutMigrations(documentUid) {
        const documentData = this.documents[documentUid];
        if (!documentData) {
            return undefined;
        }
        return {
            documentData,
            metadata: this.getMockMetadata(),
        };
    }
    async getWithoutMigrations(documentUid) {
        const documentData = this.documents[documentUid];
        if (!documentData) {
            return undefined;
        }
        return {
            documentData,
            metadata: this.getMockMetadata(),
        };
    }
    async getDataWithoutMigrations(documentUid) {
        return this.documents[documentUid];
    }
    async getBaseDataWithoutMigrations(documentUid) {
        const result = this.documents[documentUid];
        if (result) {
            return intoBaseDocument(result);
        }
        return undefined;
    }
    async getAllBaseDataByPublicStarCount(_minimumStarCount) {
        return [];
    }
    /** @inheritdoc */
    async getProperties(_documentUid, propertyNames) {
        return propertyNames.reduce((acc, propertyName) => {
            acc[propertyName] = null;
            return acc;
        }, {});
    }
    /** @inheritdoc */
    async getAllProperties(documentUid) {
        const document = this.documents[documentUid];
        if (!document) {
            return {};
        }
        return document.properties;
    }
    /** @inheritdoc */
    async getRoles(documentUid) {
        const document = this.documents[documentUid];
        if (!document) {
            return {};
        }
        return document.roles;
    }
    async *getAllEditorsForPrivateOrganizationDocs(organizationUid) {
        for (const document of Object.values(this.documents)) {
            if (document.organization_owner_uid !== organizationUid) {
                continue;
            }
            yield document.owner_uid;
            for (const role of Object.values(document.roles)) {
                if (role.permission_type === PermissionType.edit && role.user_uid !== ANON_ROLE_UID) {
                    yield role.user_uid;
                }
            }
        }
    }
    async hasAtLeastOneOrganizationPrivateDocument(organizationUid) {
        return Object.values(this.documents).some((document) => document.organization_owner_uid === organizationUid &&
            !(document.user_access_list ?? []).includes(ANON_ROLE_UID));
    }
    async hasAtLeastOneOrganizationPrivateDocumentEditableByMembers(organizationUid) {
        return Object.values(this.documents).some((document) => document.organization_owner_uid === organizationUid &&
            !(document.user_access_list ?? []).includes(ANON_ROLE_UID) &&
            document.organization_member_permission_type === PermissionType.edit);
    }
    subscribeToLatest(_documentUid, _onChange) {
        throw new Error("Method not implemented.");
    }
    async getConfigs(baseDocumentData) {
        return this.documents[baseDocumentData.uid]?.configs ?? {};
    }
    getMockMetadata() {
        return {
            codeVersion: "HEAD",
            source: "mock",
            storageId: guid(),
            schemaVersion: 99,
        };
    }
}
//# sourceMappingURL=MockDocumentRepository.js.map